import { store } from "../store/store";
import axios from "axios";
import { resetUserSession } from '../../containers/login/loginSlice'
import { config } from "../utils/config";

let API_URL = "/api"
switch (config.env) {
    case "STAG":
        API_URL = "http://localhost:8089/api"
        break
    case "PROD":
        console.log = console.warn = console.error = () => { }
        API_URL = "https://v365.groupe-vital.com/api"
        break
    case "PREPROD":
        console.log = console.warn = console.error = () => { }
        API_URL = "https://ppdv365.groupe-vital.com/api"
        break
}

const extName = {
    'text/html': 'html',
    'text/plain': 'txt',
    'image/gif': 'gif',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/svg+xml': 'svg',
    'audio/mp3': 'mp3',
    'audio/aac': 'aac',
    'audio/x-wav': 'wav',
    'video/mp4': 'mp4',
    'text/csv': 'csv',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        'pptx',
    'application/pdf': 'pdf',
}

export default class NetworkManager {

    pendingRequests = []

    static getInstance() {
        if (!this.instance) {
            this.instance = new NetworkManager()
        }
        return this.instance;
    }

    getBaseUrl() {
        return API_URL
    }

    getMediaStorageUrl() {
        return API_URL + "/v1/planning"
    }

    getUploadUrl() {
        return this.getMediaStorageUrl() + "/upload"
    }

    getDownloadUrl() {
        return this.getMediaStorageUrl() + "/downloadReport/"
    }

    /**
     * URL of a collab's profile picture, for use as an <img src>.
     *
     * Not fetched through postRequest/getRequest on purpose. Letting the browser load it as
     * an image is what buys the caching: the backend answers with a long-lived
     * `immutable` Cache-Control, so a face is fetched once and then comes from disk cache
     * on every later page load. Pulling the same bytes through axios into a data URL would
     * re-download all of them on every visit.
     *
     * `photoUpdatedAt` is what makes that safe to cache so aggressively — it changes when
     * the collab uploads a new picture, which changes the URL, so the stale entry is simply
     * never requested again. Passing null returns null: a collab with no picture has no URL
     * to load, and the caller renders initials instead of firing a request that 404s.
     *
     * @param {number|string} idCollab
     * @param {string|number|null} photoUpdatedAt `photo_updated_at` from a collab listing
     * @returns {string|null}
     */
    getCollabPhotoUrl(idCollab, photoUpdatedAt) {
        if (!idCollab || !photoUpdatedAt) return null
        // Numeric so the cache key stays stable regardless of how the timestamp was
        // serialized (listings return a SQL string, setMyPhoto returns one too, but a
        // caller holding a Date would otherwise produce a different URL for the same photo).
        const version = new Date(photoUpdatedAt).getTime()
        if (!version) return null
        return `${API_URL}${config.apiPaths.collabs.photo}/${idCollab}?v=${version}`
    }

    /**
     * 
     * @param {*} params 
     * @returns 
     */
    parseGetParams(params) {
        if (params) {
            let arrayOfParams = []
            for (let key in params) {
                arrayOfParams.push(key + "=" + params[key])
            }
            if (arrayOfParams.length)
                return "?" + arrayOfParams.join("&")
        }
        return ""
    }

    /**
     * 
     * @param {*} path 
     * @param {*} params 
     * @returns 
     */
    async postRequest(path, params) {

        const jsonParams = JSON.stringify(params)
        const options = {
            headers: {
                'lang': localStorage.getItem("lang"),
                'Content-Type': 'application/json'
            },
            withCredentials: true
        }
        try {
            if (this.pendingRequests.includes(path)) {
                console.log("duplicate call !")
                console.log(path)
            } else {
                this.pendingRequests.push(path)
            }
            //console.log('POST REQUEST '+this.getBaseUrl() + path + " PARAMS: " + jsonParams)
            const response = await axios.post(this.getBaseUrl() + path, jsonParams, options)
            if (response.api_status === 401) {
                store.dispatch(resetUserSession({ doReload: path !== config.apiPaths.user.login }))
            }
            this.pendingRequests = this.pendingRequests.filter(p => p != path)
            return { responseData: response.data, body: params }
        } catch (error) {
            if (error.response.status === 401) {
                store.dispatch(resetUserSession({ doReload: path !== config.apiPaths.user.login }))
            }
            return { responseError: error.response }
        }
    }

    /**
     * 
     * @param {*} path 
     * @param {*} params 
     * @param {*} token 
     * @returns 
     */
    async getRequest(path, params, token) {
        const options = {
            headers: {
                'lang': localStorage.getItem("lang"),
                'Content-Type': 'application/json',
                'Authorization': `${token ? token : localStorage.getItem("AUTHORIZATION")}`
            },
            withCredentials: true
        }
        try {
            if (this.pendingRequests.includes(path)) {
                console.log("duplicate call !")
                console.log(path)
            } else {
                this.pendingRequests.push(path)
            }
            let url = this.getBaseUrl() + path + this.parseGetParams(params)
            const response = await axios.get(url, options)
            if (response.api_status === 401) {
                store.dispatch(resetUserSession({ doReload: path !== config.apiPaths.user.msAuth }))
            }
            this.pendingRequests = this.pendingRequests.filter(p => p != path)
            return { responseData: response.data }
        } catch (error) {
            if (error.response.status === 401) {
                store.dispatch(resetUserSession({ doReload: path !== config.apiPaths.user.msAuth }))
            }
            return { responseError: error.response }
        }
    }

    /**
     * 
     * @param {*} file 
     * @param {*} fileName 
     * @param {*} apiKey 
     * @returns 
     */
    async uploadFile(file, fileName, apiKey) {
        if (!file) return Promise.resolve(null)

        const base64Response = await fetch(file);
        const blob = await base64Response.blob();
        let fileType = blob.type
        if (fileType) fileName += "." + extName[fileType]
        var formData = new FormData();
        formData.append('file', blob, fileName)

        const options = {
            headers: {
                'lang': localStorage.getItem("lang"),
                'Content-Type': 'multipart/form-data',
                "api-key": apiKey,
            }
        }
        try {
            const response = await axios.post(this.getUploadUrl(), formData, options);
            if (response && response.data && response.data.filename) {
                return Promise.resolve(this.getDownloadUrl() + response.data.filename)
            }
            return Promise.resolve(null)
        } catch (error) {
            return Promise.resolve(null)
        }
    }

    async uploadAssets(file, tableName, idParent, filename) {
        if (!file) return Promise.resolve(null);

        const formData = new FormData();
        formData.append('file', file);

        const options = {
            headers: {
                'lang': localStorage.getItem("lang")
            },
            withCredentials: true,
        };

        try {
            const url = `${this.getBaseUrl()}/v1/assets/upload/${tableName}/${idParent}/${filename}`;
            const response = await axios.post(url, formData, options);

            if (response && response.data) {
                return response.data;
            }
            return Promise.reject("File upload failed");
        } catch (error) {
            console.error("Error in uploadFile:", error);
            return Promise.reject(error);
        }
    }

    /**
     * 
     * @param {*} filename 
     * @returns 
     */
    async downloadFile(filename) {
        if (!filename) return Promise.resolve(null)

        const options = {
            headers: {
                'lang': localStorage.getItem("lang")
            },
            withCredentials: true,
            responseType: 'blob'
        }
        try {
            const response = await axios.get(`${this.getDownloadUrl()}${filename}`, options)
            if (response && response.data) {
                var data = new Blob([response.data])
                var excelURL = window.URL.createObjectURL(data)
                let tempLink = document.createElement('a')
                tempLink.href = excelURL
                tempLink.setAttribute('download', filename)
                tempLink.click()
                return Promise.resolve(true)
            }
            return Promise.resolve(null)
        } catch (error) {
            return Promise.resolve(null)
        }
    }

    /**
     * Function to fetch and download a file from a post request
     * @param {*} path 
     * @param {*} params 
     * @param {*} filename 
     * @returns 
     */
    async fetchFile(path, params, filename) {
        const jsonParams = JSON.stringify(params)
        const options = {
            headers: {
                'lang': localStorage.getItem("lang"),
                'Content-Type': 'application/json'
            },
            withCredentials: true,
            responseType: 'blob'
        }
        try {
            const response = await axios.post(this.getBaseUrl() + path, jsonParams, options)
            if (response && response.data) {
                var data = new Blob([response.data])
                var fileURL = window.URL.createObjectURL(data)
                let tempLink = document.createElement('a')
                tempLink.href = fileURL
                tempLink.setAttribute('download', filename)
                tempLink.click()
                return Promise.resolve(true)
            }
            return Promise.reject(response)
        } catch (error) {
            return Promise.reject(error)
        }
    }

    /**
     * function for downloading assets receiving the file creating a link for it and clicking it
     * @param {*} path 
     * @param {*} filename 
     * @returns 
     */
    async downloadAssetFile(path, filename) {
        const options = {
            headers: {
                'lang': localStorage.getItem("lang"),
                'Content-Type': 'application/json'
            },
            withCredentials: true,
            responseType: 'blob'
        }
        try {
            const response = await axios.get(this.getBaseUrl() + path, options);
            if (response && response.data) {
                var data = new Blob([response.data]);
                var fileURL = window.URL.createObjectURL(data);
                let tempLink = document.createElement('a');
                tempLink.href = fileURL;
                tempLink.setAttribute('download', filename);
                tempLink.click();
                return Promise.resolve(true);
            }
            return Promise.reject(response);
        } catch (error) {
            return Promise.reject(error);
        }
    }

}